import React, { useState, useEffect } from 'react';
import Prayer from '../group/Prayer';
import axios from 'axios';
import config from '../../config';
import '../../styles/PublicCommunity.css';

/**
 * PrayerTab Component - Public View
 * Desktop: Uses Prayer component (weekly calendar grid) with edit controls disabled
 * Mobile: Custom mobile-first design matching YesodNative
 */
const PrayerTab = ({ community }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [agendaData, setAgendaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (community?.id && isMobile) {
      fetchMobilePrayerData();
    }
  }, [community, isMobile]);

  const fetchMobilePrayerData = async () => {
    try {
      setLoading(true);
      
      const today = new Date();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - today.getDay());
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);
      
      const startDate = sunday.toISOString().split('T')[0];
      const endDate = saturday.toISOString().split('T')[0];

      // Only fetch agenda items - prayer times are now part of agenda
      const agendaResponse = await axios.get(
        `${config.API_URL}/groups/${community.id}/agenda/public?startDate=${startDate}&endDate=${endDate}`
      );

      if (agendaResponse.data.success) {
        setAgendaData(agendaResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching prayer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNext7Days = () => {
    const days = [];
    const today = new Date();
    
    // Calculate how many days until next Sunday (0 = Sunday)
    const currentDay = today.getDay();
    const daysUntilSunday = currentDay === 0 ? 0 : -currentDay;
    
    // Start from this coming Sunday
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + daysUntilSunday);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dayOfWeek = date.getDay();
      days.push({
        dayOfWeek,
        dayName: getDayName(dayOfWeek),
        date: date.getDate(),
        month: date.getMonth() + 1,
        isToday: date.toDateString() === today.toDateString()
      });
    }
    
    return days;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayOfWeek === 6 ? 'Shabbat' : days[dayOfWeek];
  };

  const getDayAbbrev = (dayOfWeek) => {
    const abbrevs = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sha'];
    return abbrevs[dayOfWeek];
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) {
      return timeStr;
    }

    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours, 10);
      const min = minutes || '00';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      return `${hour12}:${min} ${ampm}`;
    } catch (error) {
      return timeStr;
    }
  };

  const getZmanimDisplayName = (zmanimType) => {
    const zmanimDisplayNames = {
      'sunrise': 'Sunrise',
      'sunset': 'Sunset',
      'sofZmanShma': 'Latest Shema',
      'sofZmanTfilla': 'Latest Shacharit',
      'chatzot': 'Midday',
      'minchaGedola': 'Mincha Gedola',
      'minchaKetana': 'Mincha Ketana',
      'plagHaMincha': 'Plag HaMincha',
      'alotHaShachar': 'Alot HaShachar',
      'misheyakir': 'Misheyakir',
      'tzeit7083deg': 'Tzeit (3 stars)',
      'tzeit72min': 'Tzeit (Rabbeinu Tam)',
    };
    return zmanimDisplayNames[zmanimType] || zmanimType;
  };

  const formatZmanimText = (item) => {
    if (!item.zmanim_rule_enabled || !item.zmanim_type) {
      return '';
    }

    const zmanimName = getZmanimDisplayName(item.zmanim_type);
    const minutes = item.zmanim_offset_minutes || 0;
    const direction = item.zmanim_offset_direction || 'after';

    if (minutes === 0) {
      return zmanimName;
    }

    return `${minutes} min${minutes !== 1 ? '' : ''} ${direction} ${zmanimName}`;
  };

  const renderMobileView = () => {
    const days = getNext7Days();
    const selectedDayData = days.find(d => d.dayOfWeek === selectedDay);
    const dayName = selectedDayData?.dayName || getDayName(selectedDay);

    // Get agenda items for selected day
    // agendaData structure: { Sunday: { shacharit: [], mincha: [], maariv: [] }, ... }
    const daySchedule = agendaData?.[dayName] || {};
    
    // Flatten prayer types into single array
    const dayAgenda = [];
    if (typeof daySchedule === 'object' && !Array.isArray(daySchedule)) {
      // Handle grouped structure: { shacharit: [], mincha: [], maariv: [] }
      const prayerTypes = ['shacharit', 'mincha', 'maariv'];
      prayerTypes.forEach(prayerType => {
        const items = daySchedule[prayerType] || [];
        dayAgenda.push(...items);
      });
    } else if (Array.isArray(daySchedule)) {
      // Handle already-flattened array (legacy)
      dayAgenda.push(...daySchedule);
    }

    // Map agenda items to display format
    const allItems = dayAgenda.map((item, index) => ({
      id: `agenda-${index}`,
      type: 'agenda',
      title: item.title,
      time: item.time || '',
      description: item.details, // Use 'details' field from database
      display_order: item.display_order,
      zmanim_rule_enabled: item.zmanim_rule_enabled,
      zmanim_type: item.zmanim_type,
      zmanim_offset_minutes: item.zmanim_offset_minutes,
      zmanim_offset_direction: item.zmanim_offset_direction,
      isOverride: item.isOverride
    }));

    // Sort by display_order first, then by time
    allItems.sort((a, b) => {
      // First sort by display_order if both have it
      const orderA = a.display_order !== null && a.display_order !== undefined ? a.display_order : 999;
      const orderB = b.display_order !== null && b.display_order !== undefined ? b.display_order : 999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Then sort by time chronologically
      const getTimeValue = (timeStr) => {
        if (!timeStr) return 99999; // No time goes last
        const cleanTime = timeStr.replace(/[^0-9:]/g, '');
        const [hours, minutes] = cleanTime.split(':').map(n => parseInt(n) || 0);
        return hours * 60 + minutes;
      };
      
      return getTimeValue(a.time) - getTimeValue(b.time);
    });

    return (
      <div className="prayer-mobile-container">
        {/* Day Selector */}
        <div className="prayer-mobile-days">
          {days.map((day) => (
            <button
              key={day.dayOfWeek}
              className={`prayer-mobile-day ${selectedDay === day.dayOfWeek ? 'active' : ''} ${day.isToday ? 'today' : ''}`}
              onClick={() => setSelectedDay(day.dayOfWeek)}
            >
              <div className="prayer-mobile-day-abbrev">{getDayAbbrev(day.dayOfWeek)}</div>
              <div className="prayer-mobile-day-date">{day.date}</div>
            </button>
          ))}
        </div>

        {/* Prayer Items */}
        <div className="prayer-mobile-content">
          {allItems.length > 0 ? (
            allItems.map(item => (
              <div key={item.id} className="prayer-mobile-item">
                <div className="prayer-mobile-item-header">
                  <div className="prayer-mobile-item-left">
                    <div className="prayer-mobile-item-title">{item.title}</div>
                    {item.description && (
                      <div className="prayer-mobile-item-description">{item.description}</div>
                    )}
                    {item.services && (
                      <div className="prayer-mobile-item-services">{item.services}</div>
                    )}
                  </div>
                  <div className="prayer-mobile-item-right">
                    {item.time && (
                      <div className="prayer-mobile-item-time">
                        {item.zmanim_rule_enabled && <span className="prayer-mobile-time-tilde">~</span>}
                        {item.time}
                      </div>
                    )}
                  </div>
                </div>
                
                {item.zmanim_rule_enabled && item.zmanim_type && (
                  <div className="prayer-mobile-zmanim-badge">
                    {formatZmanimText(item)}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="prayer-mobile-empty">
              <p>No schedule for {dayName}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!community?.id) {
    return (
      <div className="prayer-loading">
        <div className="loading-spinner"></div>
        <p>Loading prayer schedule...</p>
      </div>
    );
  }

  if (isMobile) {
    if (loading) {
      return (
        <div className="prayer-loading">
          <div className="loading-spinner"></div>
          <p>Loading prayer schedule...</p>
        </div>
      );
    }

    return renderMobileView();
  }

  // Desktop view - use Prayer component with disabled interactions
  return (
    <div className="public-prayer-wrapper desktop" onClick={(e) => {
      // Prevent all clicks from bubbling to prayer items
      if (e.target.closest('.prayer-item') || 
          e.target.closest('.day-column-cell') ||
          e.target.closest('.agenda-item')) {
        e.stopPropagation();
        e.preventDefault();
      }
    }}>
      <Prayer 
        groupId={community.id}
        isAdmin={false}
        searchQuery=""
        highlightedQuery=""
        onDataLoad={() => {}}
        filteredData={null}
      />
    </div>
  );
};

export default PrayerTab;
